import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "bot-test-tool" }
const _hoisted_2 = {
  class: "bot-test-tool__content bot-test-tool__content--phone",
  key: "phone"
}
const _hoisted_3 = {
  class: "bot-test-tool__content",
  key: "web-assistant"
}

import { botsApi } from '../../services/bots';
import ConversationControlBar from './ConversationControlBar.vue';
import BrowserUrlBar from './BrowserUrlBar.vue';
import WebAssistantPreview from '@/features/webassistant/components/WebAssistantPreview.vue';
import PhonePreview from './PhonePreview.vue';
import BotPausedBanner from './BotPausedBanner.vue';
import { ConversationMessage } from '@/features/webassistant/models/conversation-message';
import { MessageStatus } from '@/features/webassistant/models/messageStatus';
import { MessageDirection } from '@/features/webassistant/models/messageDirection';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import { nanoid } from 'nanoid';
import { AppCard } from '@/features/apps/shared/models/appCard';
import { ExecuteTestBotViewModel } from '@/features/bots/models/request/executeTestBotViewModel';
import { BotUserViewModel } from '@/features/bots/models/botUserViewModel';
import { BotTestResponseViewModel } from '@/features/bots/models/botTestResponseViewModel';
import { InstructionViewModel, SendAppInstructionViewModel } from '@/features/bots/models/instructionViewModel';
import ContactNameUtil from '@/features/bots/contactNameUtil';
import { BotAppInitViewModel } from '@/features/bots/models/request/botAppInitViewModel';
import { BotAppSubmitViewModel } from '@/features/bots/models/request/botAppSubmitViewModel';
import appInsights from '@/shared/services/appInsights';
import { AnswerListItemViewModel } from '@/features/bots/models/answers/answerListItemViewModel';
import { useStore } from 'vuex';
import { computed, nextTick, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'BotPreviewMessagingClientContainer',
  props: {
  currentTime: {
    type: Date,
    required: true,
  },
  user: {
    type: Object as PropType<BotUserViewModel>,
    required: true,
  },
  flows: {
    type: Array as PropType<FlowListItemViewModel[]>,
    required: true,
  },
  answers: {
    type: Array as PropType<AnswerListItemViewModel[]>,
    required: true,
  },
},
  emits: ["userUpdated", "channelChanged"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const messages = ref<ConversationMessage[]>([]);
const sessionId = ref('');
const variables = ref<{ [key: string]: any }>({});
const isConversationVisible = ref(true);
const isBotPaused = ref(false);
const currentUser = ref<BotUserViewModel>({ id: '', data: {} });
const currentUrl = ref('http://home');
const currentChannelType = ref('web-assistant');
const currentChannelIdentityId = ref('web-assistant-default');
const postbackTimers = ref<number[]>([]);

const previewComponent = ref<typeof WebAssistantPreview | null>(null);

const isWebAssistant = computed(() => currentChannelType.value === 'web-assistant');
const flowsAndPriorities = computed(() => [
  ...props.flows.map((f) => ({ id: f.id, priority: f.priority })),
  ...props.answers.map((f) => ({ id: f.id, priority: 1 })),
]);

watch(
  () => props.user,
  (value) => {
    if (props.user) {
      currentUser.value = value;
    }
    emit('userUpdated', value);
  },
  { immediate: true, deep: true },
);

onMounted(async () => {
  sessionId.value = nanoid(7);
  currentUser.value.data['appUser.currentUrl'] = currentUrl.value;
  await sendPostback('widget:load');
  await sendPostback('conversation:open');
  await setupTimeOnPageTimers();
  document.addEventListener('fresco-panel-submit', cardSubmitHandler);
});

onBeforeUnmount(() => {
  document.removeEventListener('fresco-panel-submit', cardSubmitHandler);
  deletePostbackTimers();
  deleteSession();
});

async function handleMessageSend(message: string) {
  const sentMessage = addOutboundMessage(message);
  if (isBotPaused.value) {
    return;
  }
  try {
    const request: ExecuteTestBotViewModel = {
      sessionId: sessionId.value,
      event: {
        type: 'message:appUser',
        source: {
          id: currentChannelIdentityId.value,
          type: currentChannelType.value,
        },
        data: {
          'message.text': message,
          'system.currentTimeUtc': props.currentTime.toISOString(),
        },
      },
      user: currentUser.value,
      flows: flowsAndPriorities.value,
      variables: variables.value,
    };
    const response = await botsApi.sendTestBotEvent(currentWorkspace.value.id, request);
    sentMessage.status = MessageStatus.DeliverySucceeded;
    await handleBotResponse(response, true);
  } catch (e) {
    await handleError();
  } finally {
    trackTestBotEventSent('message:appUser');
  }
}

async function sendConversationHandover() {
  try {
    const request: ExecuteTestBotViewModel = {
      sessionId: sessionId.value,
      event: {
        type: 'conversation:handover',
        source: {
          id: currentChannelIdentityId.value,
          type: currentChannelType.value,
        },
        data: {
          'system.currentTimeUtc': props.currentTime.toISOString(),
        },
      },
      user: currentUser.value,
      flows: flowsAndPriorities.value,
      variables: variables.value,
    };
    deletePostbackTimers();
    const response = await botsApi.sendTestBotEvent(currentWorkspace.value.id, request);
    await handleBotResponse(response);
  } catch (e) {
    await handleError();
  } finally {
    trackTestBotEventSent('conversation:handover');
  }
}

async function sendPostback(postbackType: string) {
  if (isBotPaused.value) {
    return;
  }
  try {
    const request: ExecuteTestBotViewModel = {
      sessionId: sessionId.value,
      event: {
        type: 'postback',
        source: {
          id: currentChannelIdentityId.value,
          type: currentChannelType.value,
        },
        data: {
          'postback.payload': postbackType,
          'system.currentTimeUtc': props.currentTime.toISOString(),
        },
      },
      user: currentUser.value,
      flows: flowsAndPriorities.value,
      variables: variables.value,
    };
    const response = await botsApi.sendTestBotEvent(currentWorkspace.value.id, request);
    await handleBotResponse(response);
  } catch (e) {
    await handleError();
  } finally {
    trackTestBotEventSent('postback');
  }
}

function toggleConversationVisibility() {
  isConversationVisible.value = !isConversationVisible.value;
  if (isConversationVisible.value) {
    sendPostback('conversation:open');
  }
}

function handleQuickReply(quickReply: any) {
  handleMessageSend(quickReply.text);
}

async function urlChanged(url: string) {
  currentUrl.value = url;
  currentUser.value.data['appUser.currentUrl'] = url;
  appInsights.trackEvent({ name: 'Bot Preview: Url changed' }, { workspaceId: currentWorkspace.value.id, url });
  await resetPostbacks();
}

async function channelChanged(type: string, id: string) {
  emit('channelChanged');
  const channelChanged = currentChannelType.value !== type || currentChannelIdentityId.value !== id;
  if (channelChanged) {
    appInsights.trackEvent(
      { name: 'Bot Preview: Channel changed' },
      { workspaceId: currentWorkspace.value.id, channelId: id, channelType: type },
    );
    currentChannelType.value = type;
    currentChannelIdentityId.value = id;
    reset();
    if (currentChannelType.value === 'web-assistant') {
      await sendPostback('widget:load');
      if (isConversationVisible.value) {
        await sendPostback('conversation:open');
      }
      await setupTimeOnPageTimers();
    }
  }
}

async function refreshClicked() {
  appInsights.trackEvent({ name: 'Bot Preview: Refresh clicked' }, { workspaceId: currentWorkspace.value.id });
  await resetPostbacks();
}

function resetClicked() {
  resetConversation();
}

function resumeBot() {
  isBotPaused.value = false;
}

function addOutboundMessage(message: string): ConversationMessage {
  const sentMessage: ConversationMessage = {
    id: nanoid(7),
    type: 'text',
    text: message,
    direction: MessageDirection.Outbound,
    timestampUtc: props.currentTime,
    status: MessageStatus.Sent,
  };
  messages.value.push(sentMessage);
  scrollToBottom();
  return sentMessage;
}

async function resetPostbacks() {
  deletePostbackTimers();
  await sendPostback('widget:load');
  await setupTimeOnPageTimers();
}

async function handleBotResponse(response: BotTestResponseViewModel, isFromAppUserMessageRequest = false) {
  currentUser.value = getCurrentUser(response);
  if (isFromAppUserMessageRequest) {
    currentUser.value.data['appUser.lastHeardFrom'] = new Date();
  }

  variables.value = response.variables;
  emit('userUpdated', currentUser.value);
  await handleBotInstructions(response);
  scrollToBottom();
}

function getCurrentUser(response: BotTestResponseViewModel) {
  const newUser = response.user;
  if (!newUser) {
    return newUser;
  }

  newUser.data['appUser.firstSeen'] = currentUser.value.data['appUser.firstSeen'] || new Date();
  newUser.data['appUser.lastSeen'] = new Date();

  for (const [key, value] of Object.entries(newUser.data)) {
    if (key === 'appUser.name') {
      newUser.data['appUser.givenName'] = ContactNameUtil.extractGivenName(value);
      newUser.data['appUser.surname'] = ContactNameUtil.extractSurname(value);
    }
  }

  if (containsMessageInstructions(response)) {
    newUser.data['appUser.lastContacted'] = new Date();
  }

  return newUser;
}

function containsMessageInstructions(response: BotTestResponseViewModel) {
  return (
    response.instructions &&
    response.instructions.length > 0 &&
    response.instructions.filter((instruction) => !instruction.whisper).length > 0
  );
}

async function handleBotInstructions(botResponse: BotTestResponseViewModel) {
  for (const instruction of botResponse.instructions) {
    if (instruction.sendApp) {
      await handleAppInstruction(instruction.sendApp, botResponse.variables, botResponse.user);
    } else if (instruction.handover) {
      isBotPaused.value = true;
      await sendConversationHandover();
    } else {
      messages.value.push(
        createTextConversationMessage(
          getMessageType(instruction),
          getMessageText(instruction),
          instruction.ask ? instruction.ask.options : null,
        ),
      );
      scrollToBottom();
    }
  }
}

async function handleAppInstruction(
  appInstruction: SendAppInstructionViewModel,
  variables: { [p: string]: any },
  user: BotUserViewModel,
) {
  const context = { location: 'bot-preview', channel: currentChannelType.value };
  const botAppInitViewModel = {
    user: getAppUserFromUser(user),
    config: appInstruction.config,
    context,
    data: variables,
  } as BotAppInitViewModel;

  const appResponse = await botsApi.botAppInit(
    currentWorkspace.value.id,
    appInstruction.installedAppId,
    botAppInitViewModel,
  );
  if (appResponse.card) {
    const conversationMessage = createAppConversationMessage(appResponse.card, appInstruction.installedAppId);
    messages.value.push(conversationMessage);
    scrollToBottom();
  } else if (appResponse.flow) {
    appResponse.flow.messages.forEach((appMsg) =>
      messages.value.push(createTextConversationMessage('text', appMsg.text)),
    );
    scrollToBottom();
  }
  await initiateNextBotTurn();
}

async function initiateNextBotTurn() {
  try {
    const request: ExecuteTestBotViewModel = {
      sessionId: sessionId.value,
      event: {
        type: 'app:response',
        source: {
          id: currentChannelIdentityId.value,
          type: currentChannelType.value,
        },
        data: {
          'system.currentTimeUtc': props.currentTime.toISOString(),
        },
      },
      user: currentUser.value,
      flows: flowsAndPriorities.value,
      variables: variables.value,
    };
    const response = await botsApi.sendTestBotEvent(currentWorkspace.value.id, request);
    await handleBotResponse(response);
  } catch (e) {
    await handleError();
  } finally {
    trackTestBotEventSent('app:response');
  }
}

async function handleError() {
  await deleteSession();
  messages.value.push(createTextConversationMessage('text', 'Something went wrong, please try again'));
  scrollToBottom();
}

async function deleteSession() {
  await botsApi.deleteSession(currentWorkspace.value.id, sessionId.value);
}

function createTextConversationMessage(
  type: string,
  text: string,
  actions: string[] | null = null,
): ConversationMessage {
  appInsights.trackEvent({ name: 'Bot Preview: Message received' }, { workspaceId: currentWorkspace.value.id, type });
  return {
    id: nanoid(7),
    type,
    text,
    direction: MessageDirection.Inbound,
    status: MessageStatus.DeliverySucceeded,
    timestampUtc: props.currentTime,
    actions,
  };
}

function createAppConversationMessage(card: AppCard, installedAppId: string): ConversationMessage {
  const id = nanoid(7);
  return {
    id,
    type: 'app',
    direction: MessageDirection.Inbound,
    status: MessageStatus.DeliverySucceeded,
    timestampUtc: props.currentTime,
    card: { id, content: card },
    app: {
      installedAppId,
    },
  } as ConversationMessage;
}

function getMessageType(instruction: InstructionViewModel): string {
  if (instruction.say || instruction.ask) {
    return 'text';
  } else if (instruction.whisper) {
    return 'whisper';
  } else if (instruction.sendApp) {
    return isWebAssistant.value ? 'app' : 'text';
  }
  return '';
}

function getMessageText(instruction: InstructionViewModel): string {
  if (instruction.say) {
    return instruction.say.text;
  } else if (instruction.ask) {
    return instruction.ask.text;
  } else if (instruction.whisper) {
    return instruction.whisper.text;
  }
  return '';
}

function reset() {
  currentUser.value = { id: '', data: {} };
  currentUser.value.data['appUser.currentUrl'] = currentUrl.value;

  resetConversation();
}

function resetConversation() {
  deleteSession();

  messages.value = [];
  sessionId.value = nanoid(7);
  variables.value = {};
  isBotPaused.value = false;

  deletePostbackTimers();
}

function scrollToBottom() {
  nextTick(() => {
    if (previewComponent.value) {
      const container = previewComponent.value.$el.getElementsByClassName('n-conversation-thread')[0];
      container.scrollTop = container.scrollHeight;
    }
  });
}

async function setupTimeOnPageTimers() {
  for (const flow of props.flows) {
    if (flow && flow.timeOnPage > 0) {
      const postbackTimer = window.setTimeout(
        () => sendPostback(`widget:load+${flow.timeOnPage}`),
        flow.timeOnPage * 1000,
      );
      postbackTimers.value.push(postbackTimer);
    }
  }
}

function deletePostbackTimers() {
  for (let i = postbackTimers.value.length - 1; i >= 0; i--) {
    window.clearTimeout(postbackTimers.value[i]);
    postbackTimers.value.splice(i, 1);
  }
}

async function cardSubmitHandler(evt: any) {
  const eventDetail = evt.detail;
  const message = messages.value.find((m) => m.id === eventDetail.panelId);
  if (message && message.app && message.app.installedAppId) {
    await submit(message, eventDetail);
  }
}

async function submit(message: ConversationMessage, eventDetail: any) {
  const submitData = await botsApi.botAppSubmit(currentWorkspace.value.id, message.app!.installedAppId, {
    user: getAppUserFromUser(currentUser.value),
    componentId: eventDetail.componentId,
    storedData: containsStoredData(message) ? message!.card!.content!.storedData : {},
    data: variables.value,
    inputData: getInputData(eventDetail.parsedValues),
    context: { location: 'bot-preview', channel: currentChannelType.value },
  } as BotAppSubmitViewModel);

  if (message.card && message.card.content) {
    message.card.content = submitData.card;
  }
}

function containsStoredData(message: ConversationMessage) {
  return message.card && message.card.content && message.card.content.storedData;
}

function getInputData(frescoValues?: any[]): { [key: string]: string } {
  const values: { [key: string]: string } = {};
  if (frescoValues) {
    for (const value of frescoValues) {
      values[value.id] = value.value;
    }
  }
  return values;
}

function getAppUserFromUser(user: BotUserViewModel) {
  if (!user) {
    return {};
  }
  return {
    id: user.id,
    givenName: user.data['appUser.givenName'],
    surname: user.data['appUser.surname'],
    email: user.data['appUser.email'],
    phoneNumber: user.data['appUser.phoneNumber'],
    createdTimeUtc: user.data['appUser.firstSeen'],
    customAttributes: getCustomAttributesFromUser(user),
  };
}

function getCustomAttributesFromUser(user: BotUserViewModel) {
  return Object.entries(user.data)
    .filter(([key, value]) => key.startsWith('appUser.customAttributes.'))
    .reduce(
      (prev, [key, value]) => {
        prev[key.replace('appUser.customAttributes.', '') as string] = value;
        return prev;
      },
      {} as { [key: string]: any },
    );
}

function trackTestBotEventSent(eventType: string) {
  appInsights.trackEvent(
    { name: 'Bot Preview: Test bot event sent' },
    { workspaceId: currentWorkspace.value.id, sessionId: sessionId.value, eventType },
  );
}

return (_ctx: any,_cache: any) => {
  const _component_n_conversation = _resolveComponent("n-conversation")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isBotPaused.value)
      ? (_openBlock(), _createBlock(BotPausedBanner, {
          key: 0,
          onResume: resumeBot
        }))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "fade-header" }, {
      default: _withCtx(() => [
        (isWebAssistant.value)
          ? (_openBlock(), _createBlock(BrowserUrlBar, {
              key: 0,
              modelValue: currentUrl.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentUrl).value = $event)),
              onUrlChanged: urlChanged,
              onRefreshClicked: refreshClicked
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", {
      class: _normalizeClass(["bot-test-tool__content-container", { 'bot-test-tool__content-container__has-header': isWebAssistant.value }])
    }, [
      _createVNode(_Transition, {
        name: "fade",
        mode: "out-in"
      }, {
        default: _withCtx(() => [
          (!isWebAssistant.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(PhonePreview, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_n_conversation, {
                      ref_key: "previewComponent",
                      ref: previewComponent,
                      messages: messages.value,
                      outboundMessageColour: "#062B80",
                      readonlyApps: false,
                      onMessageSend: handleMessageSend,
                      onSendQuickReply: handleQuickReply,
                      onLauncherButtonClick: toggleConversationVisibility
                    }, null, 8, ["messages"])
                  ]),
                  _: 1
                })
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(WebAssistantPreview, {
                  ref_key: "previewComponent",
                  ref: previewComponent,
                  messages: messages.value,
                  activeView: "conversation",
                  isOpen: isConversationVisible.value,
                  displayLauncher: true,
                  typicalResponseTime: 0,
                  actionColour: "#062B80",
                  brandColour: "#062B80",
                  headerText: currentWorkspace.value.name,
                  readonlyApps: false,
                  onMessageSend: handleMessageSend,
                  onSendQuickReply: handleQuickReply,
                  onLauncherButtonClick: toggleConversationVisibility
                }, null, 8, ["messages", "isOpen", "headerText"])
              ]))
        ]),
        _: 1
      })
    ], 2),
    _createVNode(ConversationControlBar, {
      onChannelChanged: channelChanged,
      onResetClicked: resetClicked
    })
  ]))
}
}

})